import React, { useCallback, useEffect, useMemo, useState } from 'react';

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import LogoutIcon from '@mui/icons-material/Logout';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import {
   AppBar,
   Box,
   Dialog,
   DialogContent,
   Divider,
   FormControlLabel,
   IconButton,
   ListItemIcon,
   ListItemText,
   Menu,
   MenuItem,
   Switch,
   Tooltip,
   Typography,
} from '@mui/material';
import * as Sentry from '@sentry/react';
import { Avatar, AvatarFallback } from 'components/avatar';
import ShareDialog from 'components/share-dialog';
import { useSignalRState } from 'contexts/SignalRContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
   changeLibraryVisibility,
   resetDecodedImageData,
   resetState,
   setSelectedLayer,
   stateOfGeneralSlice,
} from 'state/general/general.slice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { encodeSharedPreviewToken, SHARED_PREVIEW_TOKEN_PARAM } from 'utils/sharedPreviewLink';

import guideImage from '../../assets/images/Guide.png';

/** Preview actions + user avatar: same footprint in the app bar */
const TOOLBAR_CONTROL_PX = 40;
const toolbarIconButtonSx = {
   width: TOOLBAR_CONTROL_PX,
   height: TOOLBAR_CONTROL_PX,
   minWidth: TOOLBAR_CONTROL_PX,
   minHeight: TOOLBAR_CONTROL_PX,
   boxSizing: 'border-box' as const,
   backgroundColor: '#ffffff',
   color: '#000000',
   border: '2px solid #000000',
   transition: 'background-color 0.2s',
   '&:hover': { backgroundColor: '#e0c78d' },
};

export const Appbar = () => {
   const dispatch = useAppDispatch();
   const { isLibraryVisible, decodedThreeJSData, coatType, coatThickness } =
      useAppSelector(stateOfGeneralSlice);
   const navigate = useNavigate();
   const location = useLocation();
   const { clearFilesReady, filesReady } = useSignalRState();

   const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
   const menuOpen = Boolean(anchorEl);
   const [shareDialogOpen, setShareDialogOpen] = useState(false);
   const [shareRecipientCanEdit, setShareRecipientCanEdit] = useState(false);
   const [guideDialogOpen, setGuideDialogOpen] = useState(false);
   const selectedLayer = useAppSelector((s) => s.general.selectedLayer);

   // Helper function to sync selected layer from sessionStorage to Redux when filesReady changes
   const updateLayerFromStorage = useCallback(() => {
      if (!filesReady?.scodixFileData || filesReady.scodixFileData.length === 0) {
         return;
      }

      let layerToUse: string | null = null;
      try {
         layerToUse = sessionStorage.getItem('previewLayer');
      } catch (e) {
         // ignore
      }

      if (layerToUse) {
         const isValidStoredLayer = filesReady.scodixFileData.some((layer) => layer.Layer === layerToUse);
         if (isValidStoredLayer) {
            dispatch(setSelectedLayer(layerToUse));
            return;
         }
      }

      const firstLayer = filesReady.scodixFileData[0]?.Layer || '';
      if (firstLayer) {
         dispatch(setSelectedLayer(firstLayer));
         if (!layerToUse) {
            try {
               sessionStorage.setItem('previewLayer', firstLayer);
            } catch (e) {
               // ignore
            }
         }
      }
   }, [filesReady, dispatch]);

   useEffect(() => {
      updateLayerFromStorage();
   }, [updateLayerFromStorage]);

   useEffect(() => {
      if (
         location.pathname.startsWith('/preview') &&
         filesReady?.scodixFileData &&
         filesReady.scodixFileData.length > 0
      ) {
         updateLayerFromStorage();
      }
   }, [location.pathname, filesReady, updateLayerFromStorage]);

   const user = useMemo(() => {
      try {
         const raw = sessionStorage.getItem('user');
         return raw ? JSON.parse(raw) : null;
      } catch (e) {
         return null;
      }
   }, []);

   const initials = useMemo(() => {
      const name: string = user?.nickname || user?.email || 'User';
      return name
         .split(' ')
         .map((p: string) => p.charAt(0).toUpperCase())
         .slice(0, 2)
         .join('');
   }, [user]);

   const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
   };

   const handleCloseMenu = () => setAnchorEl(null);

   const handleSettings = () => {
      // Placeholder navigation - no settings route exists yet
      handleCloseMenu();
   };

   const handleOpenGuide = () => {
      handleCloseMenu();
      setGuideDialogOpen(true);
   };

   const handleCloseGuide = () => setGuideDialogOpen(false);
   const [debugOn, setDebugOn] = useState<boolean>(() => {
      try {
         return localStorage.getItem('debug_3d') === 'true';
      } catch (e) {
         return false;
      }
   });

   React.useEffect(() => {
      const onStorage = (e: StorageEvent) => {
         if (e.key && e.key !== 'debug_3d') return;
         try {
            setDebugOn(localStorage.getItem('debug_3d') === 'true');
         } catch (err) {
            setDebugOn(false);
         }
      };
      window.addEventListener('storage', onStorage);
      return () => window.removeEventListener('storage', onStorage);
   }, []);

   const toggleDebug = (next: boolean) => {
      try {
         localStorage.setItem('debug_3d', String(next));
         setDebugOn(next);
         // notify listeners that debug changed (same-tab listeners)
         try {
            window.dispatchEvent(
               new StorageEvent('storage', { key: 'debug_3d', newValue: String(next) } as any),
            );
         } catch (_) {
            // fallback custom event if StorageEvent not constructible
            window.dispatchEvent(new Event('storage'));
         }
      } catch (e) {
         // ignore storage errors
      }
   };
   const computedShareUrl = React.useMemo(() => {
      try {
         const raw = filesReady?.uriCMYK || filesReady?.scodixFileData?.[0]?.Uri;
         if (raw) {
            const anyUrl = new URL(raw);
            const parts = anyUrl.pathname.split('/').filter(Boolean);

            if (parts.length >= 2) {
               const folder = parts[parts.length - 2];

               // Extract the folder part (like "1762790901048_1") for the API
               const apiFolderPart = folder;

               const origin = window.location.origin;
               const token = encodeSharedPreviewToken({
                  folderId: apiFolderPart,
                  coatType: coatType || undefined,
                  thickness: coatThickness || undefined,
                  layer: selectedLayer || undefined,
                  allowEdit: shareRecipientCanEdit,
               });

               return `${origin}/preview/shared?${SHARED_PREVIEW_TOKEN_PARAM}=${encodeURIComponent(token)}`;
            }
         }
      } catch (e) {
         // ignore
      }
      return window.location.href;
   }, [filesReady, location, coatType, coatThickness, selectedLayer, shareRecipientCanEdit]);

   const handleLogout = () => {
      try {
         Sentry.setUser(null);
         sessionStorage.removeItem('accessToken');
         sessionStorage.removeItem('user');
         sessionStorage.removeItem('userId');
         try {
            window.dispatchEvent(new CustomEvent('session-user-updated', { detail: { userId: null } }));
         } catch (_) {
            // ignore dispatch errors
         }
      } catch (e) {
         // ignore storage errors
      }
      dispatch(resetState());
      navigate('/');
      handleCloseMenu();
   };

   const handleRefresh = () => {
      // Revoke blob URLs to free memory before clearing
      if (decodedThreeJSData?.printColor && decodedThreeJSData.printColor.startsWith('blob:')) {
         try {
            URL.revokeObjectURL(decodedThreeJSData.printColor);
         } catch (e) {
            void 0;
         }
      }
      if (decodedThreeJSData?.printBW && decodedThreeJSData.printBW.startsWith('blob:')) {
         try {
            URL.revokeObjectURL(decodedThreeJSData.printBW);
         } catch (e) {
            void 0;
         }
      }

      // Clear selected layer from sessionStorage when going back to upload
      try {
         sessionStorage.removeItem('previewLayer');
      } catch (e) {
         // ignore
      }
      dispatch(setSelectedLayer(''));

      dispatch(resetDecodedImageData());
      clearFilesReady();
      navigate('/upload');
   };

   const isPreviewRoute = location.pathname.startsWith('/preview');

   const openShareDialog = () => {
      setShareRecipientCanEdit(false);
      setShareDialogOpen(true);
   };

   const handleCloseShareDialog = () => {
      setShareDialogOpen(false);
   };

   const handleShare = async (emails: string[]) => {
      const shareUrl = computedShareUrl;
      try {
         if (navigator.share) {
            await navigator.share({
               title: 'Scodix Preview',
               url: shareUrl,
            });
            toast.success('Preview shared');
            setShareDialogOpen(false);
            return;
         }
         if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(shareUrl);
            toast.success(
               emails.length
                  ? `Preview link copied for ${emails.length} recipient${emails.length > 1 ? 's' : ''}`
                  : 'Preview link copied to clipboard',
            );
            setShareDialogOpen(false);
            return;
         }
         throw new Error('Share not supported');
      } catch (error) {
         // avoid noise when user cancels native share
         if ((error as Error).name === 'AbortError') return;
         toast.error('Unable to share preview link');
      }
   };

   return (
      <AppBar position='fixed' className='PrimaryAppbar'>
         <Typography component='p' className='ScodixAI'>
            Scodix RealView
         </Typography>
         <Box display='flex' alignItems='center' columnGap={2} sx={{ flex: 1, justifyContent: 'center' }} />
         <Box display='flex' alignItems='center' columnGap={1}>
            {isPreviewRoute ? (
               <Tooltip title='Share preview'>
                  <IconButton
                     onClick={openShareDialog}
                     aria-label='share-preview'
                     sx={toolbarIconButtonSx}
                  >
                     <ShareOutlinedIcon />
                  </IconButton>
               </Tooltip>
            ) : null}
            {/* Library button hidden as requested */}
            {/* <Tooltip title='Library'>
               <IconButton
                  onClick={() => dispatch(changeLibraryVisibility(!isLibraryVisible))}
                  aria-label='library'
                  sx={{ width: 40, height: 40, backgroundColor: '#e0c78d', color: '#000000', '&:hover': { backgroundColor: '#d7b86d' } }}
               >
                  <LibraryBooksIcon />
               </IconButton>
            </Tooltip> */}
            <Tooltip title='Back to upload'>
               <IconButton onClick={handleRefresh} aria-label='refresh' sx={toolbarIconButtonSx}>
                  <RefreshIcon />
               </IconButton>
            </Tooltip>
            <Avatar
               className='UserAvatar box-border size-10 min-h-10 min-w-10 shrink-0 cursor-pointer border-2 border-black'
               onClick={handleOpenMenu}
            >
               <AvatarFallback className='bg-white hover:bg-[#e0c78d] text-[#1a1a1a] text-sm font-semibold transition-colors'>
                  {initials}
               </AvatarFallback>
            </Avatar>
         </Box>
         <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleCloseMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
         >
            <MenuItem disabled>
               <ListItemIcon>
                  <AccountCircleIcon fontSize='small' />
               </ListItemIcon>
               <ListItemText primary={user?.nickname || 'User'} secondary={user?.email || ''} />
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleOpenGuide}>
               <ListItemIcon>
                  <HelpOutlineIcon fontSize='small' />
               </ListItemIcon>
               <ListItemText primary='Guide' />
            </MenuItem>
            <MenuItem onClick={handleSettings}>
               <ListItemIcon>
                  <SettingsIcon fontSize='small' />
               </ListItemIcon>
               <ListItemText primary='Settings' />
            </MenuItem>
            <MenuItem>
               <FormControlLabel
                  control={
                     <Switch
                        checked={debugOn}
                        onChange={(e) => toggleDebug(e.target.checked)}
                        color='primary'
                     />
                  }
                  label='Debug'
               />
            </MenuItem>

            <Divider />
            <MenuItem onClick={handleLogout}>
               <ListItemIcon>
                  <LogoutIcon fontSize='small' />
               </ListItemIcon>
               <ListItemText primary='Logout' />
            </MenuItem>
         </Menu>
         <Dialog
            open={guideDialogOpen}
            onClose={handleCloseGuide}
            maxWidth={false}
            PaperProps={{
               sx: {
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  margin: 2,
               },
            }}
            BackdropProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.6)' } }}
            sx={{
               '& .MuiDialog-container': {
                  alignItems: 'center',
                  justifyContent: 'center',
               },
            }}
         >
            <DialogContent
               sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  overflow: 'hidden',
               }}
            >
               <Box
                  component='img'
                  src={guideImage}
                  alt='Mouse controls guide'
                  sx={{
                     maxWidth: '100%',
                     maxHeight: '90vh',
                     objectFit: 'contain',
                  }}
               />
            </DialogContent>
         </Dialog>
         <ShareDialog
            open={shareDialogOpen}
            onClose={handleCloseShareDialog}
            onShare={handleShare}
            shareUrl={computedShareUrl}
            recipientCanEdit={shareRecipientCanEdit}
            onRecipientCanEditChange={setShareRecipientCanEdit}
            response={{
               onSuccess: () => {
                  // eslint-disable-next-line no-console
                  console.log('Email sent successfully');
               },
               onError: (error) => {
                  // eslint-disable-next-line no-console
                  console.error('Failed to send email:', error);
               },
            }}
         />
      </AppBar>
   );
};
